// FRAME (0x81) section writers and readers (protocol §5.4).
//
// Layout: `u8 0x81`, `u32 tick`, then sections until the end of the message. Each section is
// `u8 sectionId`, `vu byteLength`, payload. Sections appear in ascending id order, each at most
// once. Decoders skip unknown ids and ignore unread bytes at the end of a known section.
//
// Two levels of API:
// - Structured: `writeFrame(w, frame, quant)` / `readFrameBody(r, quant)` and one
//   `write…Section` / `read…Section` per section, using the world-unit types in types.ts.
// - Low level, for the server's frame builder and its per-tick shared record cache (protocol §6.4):
//
//     beginFrame(w, tick);
//     const s = beginSection(w, SECTION.ADD);
//     const c = w.reserveVu();               // record count, patched once known
//     for (const e of entering) {            // nearest first; stop at the 32 KiB budget
//       w.bytes(cachedAddRecordBytes(e));    // made with writeAddRecordQ into a scratch Writer
//       n++;
//     }
//     w.patchVu(c, n);                       // inner reservation first...
//     endSection(w, s);                      // ...then the section length
//
//   Positions and radii at this level are already quantised integers (see quant.ts), so the
//   server can compare them with its per-client `lastSent` values directly (protocol §6.2).

import {
  MAX_LEADERBOARD_ENTRIES,
  MAX_MINIMAP_ENTRIES,
  MAX_MONEY_ENTRIES,
  STR_MAX_BYTES,
} from '../constants.js';
import type { MatchState } from '../modes.js';
import {
  AMOUNT_KIND,
  type AmountKind,
  isKind,
  KIND,
  LEADERBOARD_FLAG,
  MINIMAP_FLAG,
  MOVE_MASK,
  PLAYER_FLAG,
  S2C,
  SECTION,
  type Kind,
  type SectionId,
} from './opcodes.js';
import {
  readArenaDropsSection,
  readArenaEventsSection,
  readArenaHeadsSection,
  readArenaLandSection,
  readArenaMinimapSection,
  readArenaOwnSection,
  readArenaTrailsSection,
  writeArenaDropsSection,
  writeArenaEventsSection,
  writeArenaHeadsSection,
  writeArenaLandSection,
  writeArenaMinimapSection,
  writeArenaOwnSection,
  writeArenaTrailsSection,
} from './arena-frame.js';
import { fractionFromWire, fractionToWire, quantizePos, quantizeRad, type Quant } from './quant.js';
import { ProtocolError, type Reader } from './reader.js';
import type {
  AddRecord,
  CameraSection,
  EatenRecord,
  FrameMessage,
  LeaderboardEntry,
  LeaderboardSection,
  MatchSection,
  MinimapEntry,
  MinimapSection,
  MoneySection,
  MoveRecord,
  OwnSection,
  PlayerRecord,
  PlayerValue,
  RoomSection,
  TeamsSection,
  ZoneInfo,
} from './types.js';
import type { Writer } from './writer.js';

const MOVE_KNOWN_BITS = MOVE_MASK.ABS | MOVE_MASK.DELTA | MOVE_MASK.RADIUS;
const MAX_MATCH_STATE = 4;

// ---------------------------------------------------------------------------------------------
// Frame assembly
// ---------------------------------------------------------------------------------------------

/** Writes the FRAME header: opcode 0x81 and the u32 tick. An empty frame is just this (5 bytes). */
export function beginFrame(w: Writer, tick: number): void {
  w.u8(S2C.FRAME).u32(tick);
}

/** Writes a section id and reserves its length; returns the mark for `endSection`. */
export function beginSection(w: Writer, id: SectionId): number {
  w.u8(id);
  return w.reserveVu();
}

/** Patches the length of the section begun at `mark` (everything written since). */
export function endSection(w: Writer, mark: number): void {
  w.patchVu(mark, w.length - mark - 1);
}

// ---------------------------------------------------------------------------------------------
// Low-level records (quantised integers)
// ---------------------------------------------------------------------------------------------

/**
 * One ADD record from quantised values. `extra` is the owner playerId for a cell (kind 0) and the
 * 0xRRGGBB colour for ejected mass (kind 2); other kinds ignore it.
 */
export function writeAddRecordQ(
  w: Writer,
  id: number,
  kind: Kind,
  qx: number,
  qy: number,
  qr: number,
  extra = 0,
): void {
  w.vu(id).u8(kind).posQ(qx).posQ(qy).radQ(qr);
  if (kind === KIND.CELL) w.vu(extra);
  else if (kind === KIND.EJECTED) w.rgb(extra);
}

/**
 * One MOVE record from quantised values. With MOVE_MASK.ABS, `a`/`b` are the quantised x/y; with
 * MOVE_MASK.DELTA they are the deltas in posQ steps (each −128..127; protocol §6.2 uses the delta
 * form only when both fit in ±127). `qr` is written only with MOVE_MASK.RADIUS.
 */
export function writeMoveRecordQ(
  w: Writer,
  id: number,
  mask: number,
  a: number,
  b: number,
  qr: number,
): void {
  if ((mask & ~MOVE_KNOWN_BITS) !== 0) throw new RangeError(`MOVE mask has unknown bits: ${mask}`);
  if ((mask & MOVE_MASK.ABS) !== 0 && (mask & MOVE_MASK.DELTA) !== 0) {
    throw new RangeError('MOVE mask has both ABS and DELTA');
  }
  w.vu(id).u8(mask);
  if ((mask & MOVE_MASK.ABS) !== 0) w.posQ(a).posQ(b);
  else if ((mask & MOVE_MASK.DELTA) !== 0) w.i8(a).i8(b);
  if ((mask & MOVE_MASK.RADIUS) !== 0) w.radQ(qr);
}

// ---------------------------------------------------------------------------------------------
// Structured records (world units)
// ---------------------------------------------------------------------------------------------

export function writePlayerRecord(w: Writer, p: PlayerRecord): void {
  const hasSkin = p.skinId !== '';
  const flags =
    (hasSkin ? PLAYER_FLAG.HAS_SKIN : 0) |
    (p.partyMember ? PLAYER_FLAG.PARTY_MEMBER : 0) |
    (p.isYou ? PLAYER_FLAG.IS_YOU : 0) |
    (p.teammate ? PLAYER_FLAG.TEAMMATE : 0) |
    (p.isBot ? PLAYER_FLAG.IS_BOT : 0);
  w.vu(p.playerId).u8(flags).rgb(p.color).u8(p.team).str(p.name, STR_MAX_BYTES.playerName);
  if (hasSkin) w.str(p.skinId, STR_MAX_BYTES.skinId);
}

export function writeAddRecord(w: Writer, a: AddRecord, quant: Quant): void {
  writeAddRecordQ(
    w,
    a.id,
    a.kind,
    quantizePos(a.x, quant.posQ),
    quantizePos(a.y, quant.posQ),
    quantizeRad(a.r, quant.radQ),
    a.kind === KIND.CELL ? a.playerId : a.kind === KIND.EJECTED ? a.color : 0,
  );
}

export function writeMoveRecord(w: Writer, m: MoveRecord, quant: Quant): void {
  const a = Math.round(m.x / quant.posQ);
  const b = Math.round(m.y / quant.posQ);
  writeMoveRecordQ(w, m.id, m.mask, a, b, quantizeRad(m.r, quant.radQ));
}

// ---------------------------------------------------------------------------------------------
// Section writers (each writes id, length and payload)
// ---------------------------------------------------------------------------------------------

export function writePlayersSection(w: Writer, players: readonly PlayerRecord[]): void {
  const s = beginSection(w, SECTION.PLAYERS);
  w.vu(players.length);
  for (const p of players) writePlayerRecord(w, p);
  endSection(w, s);
}

export function writeAddSection(w: Writer, adds: readonly AddRecord[], quant: Quant): void {
  const s = beginSection(w, SECTION.ADD);
  w.vu(adds.length);
  for (const a of adds) writeAddRecord(w, a, quant);
  endSection(w, s);
}

export function writeMoveSection(w: Writer, moves: readonly MoveRecord[], quant: Quant): void {
  const s = beginSection(w, SECTION.MOVE);
  w.vu(moves.length);
  for (const m of moves) writeMoveRecord(w, m, quant);
  endSection(w, s);
}

export function writeEatenSection(w: Writer, eaten: readonly EatenRecord[]): void {
  const s = beginSection(w, SECTION.EATEN);
  w.vu(eaten.length);
  for (const e of eaten) w.vu(e.eatenId).vu(e.eaterId);
  endSection(w, s);
}

export function writeRemoveSection(w: Writer, ids: readonly number[]): void {
  const s = beginSection(w, SECTION.REMOVE);
  w.vu(ids.length);
  for (const id of ids) w.vu(id);
  endSection(w, s);
}

export function writeOwnSection(w: Writer, own: OwnSection): void {
  const s = beginSection(w, SECTION.OWN);
  w.u32(Math.floor(own.score)).vu(own.cellIds.length);
  for (const id of own.cellIds) w.vu(id);
  endSection(w, s);
}

export function writeCameraSection(w: Writer, cam: CameraSection, quant: Quant): void {
  const s = beginSection(w, SECTION.CAMERA);
  w.pos(cam.x, quant.posQ)
    .pos(cam.y, quant.posQ)
    .u16(fractionToWire(cam.viewScale))
    .vu(cam.followedPlayerId);
  endSection(w, s);
}

export function writeLeaderboardSection(w: Writer, lb: LeaderboardSection): void {
  if (lb.entries.length > MAX_LEADERBOARD_ENTRIES) {
    throw new RangeError(
      `LEADERBOARD has ${lb.entries.length} entries (max ${MAX_LEADERBOARD_ENTRIES})`,
    );
  }
  const s = beginSection(w, SECTION.LEADERBOARD);
  w.u8(lb.entries.length);
  for (const e of lb.entries) {
    const flags =
      (e.isYou ? LEADERBOARD_FLAG.IS_YOU : 0) |
      (e.partyMember ? LEADERBOARD_FLAG.PARTY_MEMBER : 0) |
      (e.isBot ? LEADERBOARD_FLAG.IS_BOT : 0);
    w.u8(flags).str(e.name, STR_MAX_BYTES.leaderboardName);
  }
  w.u16(lb.yourRank);
  // Amounts trailer (protocol §5.4): after yourRank, so decoders that predate it skip it as
  // unread trailing bytes of a known section.
  if (lb.amountKind !== undefined) {
    w.u8(lb.amountKind);
    for (const e of lb.entries) w.vl(e.amount ?? 0);
    w.vl(lb.yourAmount ?? 0);
    // Land trailer (arena rooms, protocol §5.4): after the amounts, which it needs. Present iff
    // `yourLandPct` is set; then every entry has `landPct` (u16 hundredths of a percent).
    if (lb.yourLandPct !== undefined) {
      for (const e of lb.entries) w.u16(landPctWire(e.landPct ?? 0));
      w.u16(landPctWire(lb.yourLandPct));
      // Id trailer (arena rooms): after the land trailer, iff every entry has a player id.
      if (lb.entries.length > 0 && lb.entries.every((e) => e.playerId !== undefined)) {
        for (const e of lb.entries) w.vu(e.playerId ?? 0);
      }
    }
  } else if (lb.yourLandPct !== undefined) {
    throw new RangeError('LEADERBOARD land trailer needs the amounts trailer');
  }
  endSection(w, s);
}

/** A percent (0–100) as `u16` hundredths, clamped. */
function landPctWire(pct: number): number {
  const q = Number.isFinite(pct) ? Math.round(pct * 100) : 0;
  return q < 0 ? 0 : q > 10_000 ? 10_000 : q;
}

export function writeTeamsSection(w: Writer, teams: TeamsSection): void {
  const s = beginSection(w, SECTION.TEAMS);
  w.u8(teams.shares.length);
  for (const share of teams.shares) w.u16(fractionToWire(share));
  endSection(w, s);
}

export function writeMinimapSection(w: Writer, mm: MinimapSection, quant: Quant): void {
  if (mm.entries.length > MAX_MINIMAP_ENTRIES) {
    throw new RangeError(`MINIMAP has ${mm.entries.length} entries (max ${MAX_MINIMAP_ENTRIES})`);
  }
  const s = beginSection(w, SECTION.MINIMAP);
  w.u8(mm.entries.length);
  for (const e of mm.entries) {
    const flags =
      (e.inTop20 ? MINIMAP_FLAG.IN_TOP20 : 0) | (e.partyMember ? MINIMAP_FLAG.PARTY_MEMBER : 0);
    w.vu(e.playerId)
      .u8(flags)
      .rgb(e.color)
      .pos(e.x, quant.posQ)
      .pos(e.y, quant.posQ)
      .rad(e.r, quant.radQ);
  }
  endSection(w, s);
}

export function writeMatchSection(w: Writer, m: MatchSection, quant: Quant): void {
  const s = beginSection(w, SECTION.MATCH);
  w.u8(m.state).u16(m.stateSecondsLeft).u16(m.playersAlive).u16(m.playersNeeded);
  const z = m.zone;
  w.u8(z ? 1 : 0);
  if (z) {
    w.pos(z.cx, quant.posQ)
      .pos(z.cy, quant.posQ)
      .f32(z.r)
      .pos(z.nextCx, quant.posQ)
      .pos(z.nextCy, quant.posQ)
      .f32(z.nextR)
      .u16(z.shrinkSecondsLeft);
  }
  endSection(w, s);
}

export function writeRoomSection(w: Writer, room: RoomSection): void {
  const s = beginSection(w, SECTION.ROOM);
  w.u16(room.playing).u16(room.spectators);
  endSection(w, s);
}

export function writeMoneySection(w: Writer, m: MoneySection): void {
  if (m.values.length > MAX_MONEY_ENTRIES) {
    throw new RangeError(`MONEY has ${m.values.length} entries (max ${MAX_MONEY_ENTRIES})`);
  }
  const s = beginSection(w, SECTION.MONEY);
  w.f32(m.solUsd).vu(m.values.length);
  for (const v of m.values) w.vu(v.playerId).vl(v.value);
  endSection(w, s);
}

/** Writes a whole FRAME (header plus every present section, in id order). */
export function writeFrame(w: Writer, f: Omit<FrameMessage, 'op'>, quant: Quant): void {
  beginFrame(w, f.tick);
  if (f.players) writePlayersSection(w, f.players);
  if (f.add) writeAddSection(w, f.add, quant);
  if (f.move) writeMoveSection(w, f.move, quant);
  if (f.eaten) writeEatenSection(w, f.eaten);
  if (f.remove) writeRemoveSection(w, f.remove);
  if (f.own) writeOwnSection(w, f.own);
  if (f.camera) writeCameraSection(w, f.camera, quant);
  if (f.leaderboard) writeLeaderboardSection(w, f.leaderboard);
  if (f.teams) writeTeamsSection(w, f.teams);
  if (f.minimap) writeMinimapSection(w, f.minimap, quant);
  if (f.match) writeMatchSection(w, f.match, quant);
  if (f.room) writeRoomSection(w, f.room);
  if (f.money) writeMoneySection(w, f.money);
  if (f.arenaLand) writeArenaLandSection(w, f.arenaLand);
  if (f.arenaHeads) writeArenaHeadsSection(w, f.arenaHeads);
  if (f.arenaTrails) writeArenaTrailsSection(w, f.arenaTrails);
  if (f.arenaDrops) writeArenaDropsSection(w, f.arenaDrops);
  if (f.arenaEvents) writeArenaEventsSection(w, f.arenaEvents);
  if (f.arenaOwn) writeArenaOwnSection(w, f.arenaOwn);
  if (f.arenaMinimap) writeArenaMinimapSection(w, f.arenaMinimap);
}

// ---------------------------------------------------------------------------------------------
// Section readers (each gets a reader bounded to the section payload)
// ---------------------------------------------------------------------------------------------

/** Reads a `vu` count and rejects counts that cannot possibly fit in the remaining bytes. */
function readCount(r: Reader, minRecordBytes: number): number {
  const at = r.offset;
  const n = r.vu();
  if (n * minRecordBytes > r.remaining) throw new ProtocolError(`count ${n} exceeds the data`, at);
  return n;
}

export function readPlayersSection(r: Reader): PlayerRecord[] {
  const n = readCount(r, 7);
  const out: PlayerRecord[] = [];
  for (let i = 0; i < n; i++) {
    const playerId = r.vu();
    const flags = r.u8();
    const color = r.rgb();
    const team = r.u8();
    const name = r.str(STR_MAX_BYTES.playerName);
    const skinId = (flags & PLAYER_FLAG.HAS_SKIN) !== 0 ? r.str(STR_MAX_BYTES.skinId) : '';
    out.push({
      playerId,
      partyMember: (flags & PLAYER_FLAG.PARTY_MEMBER) !== 0,
      isYou: (flags & PLAYER_FLAG.IS_YOU) !== 0,
      teammate: (flags & PLAYER_FLAG.TEAMMATE) !== 0,
      isBot: (flags & PLAYER_FLAG.IS_BOT) !== 0,
      color,
      team,
      name,
      skinId,
    });
  }
  return out;
}

export function readAddSection(r: Reader, quant: Quant): AddRecord[] {
  const n = readCount(r, 8);
  const out: AddRecord[] = [];
  for (let i = 0; i < n; i++) {
    const id = r.vu();
    const at = r.offset;
    const kind = r.u8();
    if (!isKind(kind)) throw new ProtocolError(`unknown entity kind ${kind}`, at);
    const x = r.pos(quant.posQ);
    const y = r.pos(quant.posQ);
    const rad = r.rad(quant.radQ);
    const playerId = kind === KIND.CELL ? r.vu() : 0;
    const color = kind === KIND.EJECTED ? r.rgb() : 0;
    out.push({ id, kind, x, y, r: rad, playerId, color });
  }
  return out;
}

export function readMoveSection(r: Reader, quant: Quant): MoveRecord[] {
  const n = readCount(r, 2);
  const out: MoveRecord[] = [];
  for (let i = 0; i < n; i++) {
    const id = r.vu();
    const at = r.offset;
    const mask = r.u8();
    if ((mask & ~MOVE_KNOWN_BITS) !== 0)
      throw new ProtocolError(`MOVE mask has unknown bits: ${mask}`, at);
    if ((mask & MOVE_MASK.ABS) !== 0 && (mask & MOVE_MASK.DELTA) !== 0) {
      throw new ProtocolError('MOVE mask has both ABS and DELTA', at);
    }
    let x = 0;
    let y = 0;
    let rad = 0;
    if ((mask & MOVE_MASK.ABS) !== 0) {
      x = r.pos(quant.posQ);
      y = r.pos(quant.posQ);
    } else if ((mask & MOVE_MASK.DELTA) !== 0) {
      x = r.i8() * quant.posQ;
      y = r.i8() * quant.posQ;
    }
    if ((mask & MOVE_MASK.RADIUS) !== 0) rad = r.rad(quant.radQ);
    out.push({ id, mask, x, y, r: rad });
  }
  return out;
}

export function readEatenSection(r: Reader): EatenRecord[] {
  const n = readCount(r, 2);
  const out: EatenRecord[] = [];
  for (let i = 0; i < n; i++) out.push({ eatenId: r.vu(), eaterId: r.vu() });
  return out;
}

export function readRemoveSection(r: Reader): number[] {
  const n = readCount(r, 1);
  const out: number[] = [];
  for (let i = 0; i < n; i++) out.push(r.vu());
  return out;
}

export function readOwnSection(r: Reader): OwnSection {
  const score = r.u32();
  const n = readCount(r, 1);
  const cellIds: number[] = [];
  for (let i = 0; i < n; i++) cellIds.push(r.vu());
  return { score, cellIds };
}

export function readCameraSection(r: Reader, quant: Quant): CameraSection {
  const x = r.pos(quant.posQ);
  const y = r.pos(quant.posQ);
  const viewScale = fractionFromWire(r.u16());
  const followedPlayerId = r.vu();
  return { x, y, viewScale, followedPlayerId };
}

export function readLeaderboardSection(r: Reader): LeaderboardSection {
  const at = r.offset;
  const n = r.u8();
  if (n > MAX_LEADERBOARD_ENTRIES)
    throw new ProtocolError(`LEADERBOARD count ${n} > ${MAX_LEADERBOARD_ENTRIES}`, at);
  const entries: LeaderboardEntry[] = [];
  for (let i = 0; i < n; i++) {
    const flags = r.u8();
    entries.push({
      isYou: (flags & LEADERBOARD_FLAG.IS_YOU) !== 0,
      partyMember: (flags & LEADERBOARD_FLAG.PARTY_MEMBER) !== 0,
      isBot: (flags & LEADERBOARD_FLAG.IS_BOT) !== 0,
      name: r.str(STR_MAX_BYTES.leaderboardName),
    });
  }
  const lb: LeaderboardSection = { entries, yourRank: r.u16() };
  if (r.remaining > 0) {
    const kindAt = r.offset;
    const kind = r.u8();
    if (kind !== AMOUNT_KIND.SCORE && kind !== AMOUNT_KIND.LAMPORTS) {
      throw new ProtocolError(`unknown LEADERBOARD amount kind ${kind}`, kindAt);
    }
    lb.amountKind = kind as AmountKind;
    for (const e of entries) e.amount = r.vl();
    lb.yourAmount = r.vl();
    // Land trailer (arena rooms).
    if (r.remaining > 0) {
      for (const e of entries) e.landPct = r.u16() / 100;
      lb.yourLandPct = r.u16() / 100;
      // Id trailer (arena rooms).
      if (r.remaining > 0) for (const e of entries) e.playerId = r.vu();
    }
  }
  return lb;
}

export function readTeamsSection(r: Reader): TeamsSection {
  const n = r.u8();
  const shares: number[] = [];
  for (let i = 0; i < n; i++) shares.push(fractionFromWire(r.u16()));
  return { shares };
}

export function readMinimapSection(r: Reader, quant: Quant): MinimapSection {
  const at = r.offset;
  const n = r.u8();
  if (n > MAX_MINIMAP_ENTRIES)
    throw new ProtocolError(`MINIMAP count ${n} > ${MAX_MINIMAP_ENTRIES}`, at);
  const entries: MinimapEntry[] = [];
  for (let i = 0; i < n; i++) {
    const playerId = r.vu();
    const flags = r.u8();
    entries.push({
      playerId,
      inTop20: (flags & MINIMAP_FLAG.IN_TOP20) !== 0,
      partyMember: (flags & MINIMAP_FLAG.PARTY_MEMBER) !== 0,
      color: r.rgb(),
      x: r.pos(quant.posQ),
      y: r.pos(quant.posQ),
      r: r.rad(quant.radQ),
    });
  }
  return { entries };
}

export function readMatchSection(r: Reader, quant: Quant): MatchSection {
  const at = r.offset;
  const state = r.u8();
  if (state > MAX_MATCH_STATE) throw new ProtocolError(`unknown match state ${state}`, at);
  const stateSecondsLeft = r.u16();
  const playersAlive = r.u16();
  const playersNeeded = r.u16();
  const hasZone = r.u8() !== 0;
  let zone: ZoneInfo | null = null;
  if (hasZone) {
    zone = {
      cx: r.pos(quant.posQ),
      cy: r.pos(quant.posQ),
      r: r.f32Finite(),
      nextCx: r.pos(quant.posQ),
      nextCy: r.pos(quant.posQ),
      nextR: r.f32Finite(),
      shrinkSecondsLeft: r.u16(),
    };
  }
  return { state: state as MatchState, stateSecondsLeft, playersAlive, playersNeeded, zone };
}

export function readRoomSection(r: Reader): RoomSection {
  return { playing: r.u16(), spectators: r.u16() };
}

export function readMoneySection(r: Reader): MoneySection {
  const solUsd = r.f32Finite();
  const at = r.offset;
  const n = readCount(r, 2);
  if (n > MAX_MONEY_ENTRIES) throw new ProtocolError(`MONEY count ${n} > ${MAX_MONEY_ENTRIES}`, at);
  const values: PlayerValue[] = [];
  for (let i = 0; i < n; i++) values.push({ playerId: r.vu(), value: r.vl() });
  // Bytes after the values (the retired cell trailer of older servers) are skipped.
  return { solUsd, values };
}

/**
 * Reads a FRAME after its opcode byte: the tick, then every section to the end of the message.
 * Unknown section ids are skipped; ids out of ascending order (or repeated) are a protocol error.
 */
export function readFrameBody(r: Reader, quant: Quant): FrameMessage {
  const frame: FrameMessage = { op: S2C.FRAME, tick: r.u32() };
  let last = -1;
  while (r.remaining > 0) {
    const at = r.offset;
    const id = r.u8();
    if (id <= last) throw new ProtocolError(`FRAME section 0x${id.toString(16)} out of order`, at);
    last = id;
    const s = r.sub(r.vu());
    switch (id) {
      case SECTION.PLAYERS:
        frame.players = readPlayersSection(s);
        break;
      case SECTION.ADD:
        frame.add = readAddSection(s, quant);
        break;
      case SECTION.MOVE:
        frame.move = readMoveSection(s, quant);
        break;
      case SECTION.EATEN:
        frame.eaten = readEatenSection(s);
        break;
      case SECTION.REMOVE:
        frame.remove = readRemoveSection(s);
        break;
      case SECTION.OWN:
        frame.own = readOwnSection(s);
        break;
      case SECTION.CAMERA:
        frame.camera = readCameraSection(s, quant);
        break;
      case SECTION.LEADERBOARD:
        frame.leaderboard = readLeaderboardSection(s);
        break;
      case SECTION.TEAMS:
        frame.teams = readTeamsSection(s);
        break;
      case SECTION.MINIMAP:
        frame.minimap = readMinimapSection(s, quant);
        break;
      case SECTION.MATCH:
        frame.match = readMatchSection(s, quant);
        break;
      case SECTION.ROOM:
        frame.room = readRoomSection(s);
        break;
      case SECTION.MONEY:
        frame.money = readMoneySection(s);
        break;
      case SECTION.ARENA_LAND:
        frame.arenaLand = readArenaLandSection(s);
        break;
      case SECTION.ARENA_HEADS:
        frame.arenaHeads = readArenaHeadsSection(s);
        break;
      case SECTION.ARENA_TRAILS:
        frame.arenaTrails = readArenaTrailsSection(s);
        break;
      case SECTION.ARENA_DROPS:
        frame.arenaDrops = readArenaDropsSection(s);
        break;
      case SECTION.ARENA_EVENTS:
        frame.arenaEvents = readArenaEventsSection(s);
        break;
      case SECTION.ARENA_OWN:
        frame.arenaOwn = readArenaOwnSection(s);
        break;
      case SECTION.ARENA_MINIMAP:
        frame.arenaMinimap = readArenaMinimapSection(s);
        break;
      default:
        // Unknown section (a newer server): skipped, as required by protocol §5.4.
        break;
    }
  }
  return frame;
}
